import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import { tenantsApi, ApiError } from "@/api/client";
import type { Tenant, TenantStatus } from "@/types";

const STATUS_FILTERS: (TenantStatus | "ALL")[] = [
  "ALL",
  "ACTIVE",
  "PROVISIONING",
  "SUSPENDED",
  "FAILED",
  "SOFT_DELETED",
];

export default function Dashboard() {
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TenantStatus | "ALL">("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter === "ALL" ? undefined : { status: filter };
      const res = await tenantsApi.list(params);
      setTenants(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 15_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Workspaces</h1>
        <div style={{ flex: 1 }} />
        <select value={filter} onChange={(e) => setFilter(e.target.value as TenantStatus | "ALL")} style={{ width: 180 }}>
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={() => void load()}>Refresh</button>
        <button className="primary" onClick={() => navigate("/tenants/new")}>+ Add Workspace</button>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && tenants.length === 0 && <p className="muted">Loading...</p>}

      {tenants.length === 0 && !loading && !error && (
        <div style={{ padding: 24, background: "white", borderRadius: 8, border: "1px dashed var(--color-border)", textAlign: "center" }}>
          <p className="muted">No workspaces yet. Click <strong>+ Add Workspace</strong> to create the first one.</p>
        </div>
      )}

      {tenants.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Tenant ID</th>
              <th>Display Name</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Domain</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.tenantId}>
                <td><Link to={`/tenants/${t.tenantId}`}>{t.tenantId}</Link></td>
                <td>{t.displayName}</td>
                <td>{t.planSize}</td>
                <td><span className={`badge ${t.status}`}>{t.status}</span></td>
                <td>
                  {t.status === "ACTIVE" ? (
                    <a href={`https://${t.domain}`} target="_blank" rel="noreferrer">{t.domain}</a>
                  ) : (
                    <span className="muted">{t.domain}</span>
                  )}
                </td>
                <td className="muted">{formatDate(t.createdAt)}</td>
                <td><Link to={`/tenants/${t.tenantId}`}>Manage →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ApiError 미사용 import 경고 회피용
void ApiError;
